import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectApiConnect } from '../lib/api/projectApi.connect';
/**
 * Query keys for project chats.
 */ export const projectChatsKeys = {
    all: [
        'projectChats'
    ],
    list: (projectId)=>[
            ...projectChatsKeys.all,
            'list',
            projectId
        ],
    detail: (projectId, chatId)=>[
            ...projectChatsKeys.all,
            'detail',
            projectId,
            chatId
        ]
};
/**
 * Hook to fetch project chats list.
 */ export function useProjectChats(projectId, params) {
    return useQuery({
        queryKey: [
            'projectChats',
            projectId,
            params?.sort,
            params?.order,
            params?.search
        ],
        queryFn: ()=>{
            if (!projectId) {
                throw new Error('Project ID is required');
            }
            return projectApiConnect.listProjectChats(projectId, params);
        },
        enabled: !!projectId,
        staleTime: 30000
    });
}
/**
 * Hook to fetch a single project chat.
 */ export function useProjectChat(projectId, chatId) {
    return useQuery({
        queryKey: projectChatsKeys.detail(projectId ?? '', chatId ?? ''),
        queryFn: async ()=>{
            if (!projectId || !chatId) {
                throw new Error('Project ID and Chat ID are required');
            }
            const response = await projectApiConnect.getProjectChat(projectId, chatId);
            return response.chat;
        },
        enabled: !!projectId && !!chatId,
        staleTime: 30000
    });
}
/**
 * Hook to create a project chat.
 */ export function useCreateProjectChat() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ projectId, data })=>projectApiConnect.createProjectChat(projectId, data),
        onSuccess: (data, { projectId })=>{
            // The project row exists before the first AgentService turn is saved.
            sessionStorage.setItem(`init::${data.chat.chatId}`, JSON.stringify({}));
            queryClient.invalidateQueries({
                queryKey: [
                    'projectChats',
                    projectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'project',
                    projectId
                ]
            });
        }
    });
}
/**
 * Hook to fork a project chat.
 */ export function useForkProjectChat() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ projectId, chatId, data })=>projectApiConnect.forkProjectChat(projectId, chatId, data),
        onSuccess: (_data, { projectId })=>{
            queryClient.invalidateQueries({
                queryKey: [
                    'projectChats',
                    projectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'project',
                    projectId
                ]
            });
        }
    });
}
